import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CreateMerchantPayoutAccountRequestDto,
  PayoutAccountType,
  PayoutAccountTypeEnum,
  PayServiceTypeEnum,
  PayServiceTypeModel,
  UpdateMerchantPayoutAccountRequestDto
} from 'app/models';
import { MerchantService } from 'app/services';
import { Utils } from 'app/utils';
import { MessageService } from 'primeng/api';
@Component({
  selector: 'merchant-payment-method-modal',
  templateUrl: './merchant-payment-method-modal.component.html',
  styleUrls: ['./merchant-payment-method-modal.component.css','../../merchant-store-onboarding.component.scss']
})
export class MerchantPaymentMethodModalComponent implements OnInit {
  @Output() private showPaymentGridView = new EventEmitter<boolean>(false);
  readonly dialogRef = inject(MatDialogRef<MerchantPaymentMethodModalComponent>);

  data = inject(MAT_DIALOG_DATA);
  selectedPayoutAccountTypeValue: number = 0;
  selectedPaymentMethodValue: number = 0;
  accountNumberMaxLength: number = 12;

  payoutAccountTypes = PayoutAccountType.PayoutAccountTypeList;

  paymentMethods = PayServiceTypeModel.CommonPaymentMethods;
  paymentFormGroup: FormGroup = new FormGroup({});
  loading = false;

  constructor(private merchantService: MerchantService,
              private messageService: MessageService) { }

  ngOnInit() {
    this.initializeFields();
  }

  savePaymentMethod(){
    this.loading = true;

    if(this.data?.isCreate){
      this.createPaymentMethod();
    }
    else{
      this.updatePaymentMethod();
    }
  }

  createPaymentMethod(){
    const isPayMaya = (this.paymentFormGroup.get('bankName')?.value === PayServiceTypeEnum.PayMaya);
    const payload: CreateMerchantPayoutAccountRequestDto = {
      MerchantId: this.data?.merchantId,
      UsePaymayaAccount: isPayMaya,
      PayoutAccountTypeId: this.paymentFormGroup.get('payoutAccountType')?.value,
      WalletDetailId: 0,
      AccountName: this.paymentFormGroup.get('accountName')?.value,
      AccountNumber: this.paymentFormGroup.get('accountNo')?.value.toString(),
      PayServiceId: this.paymentFormGroup.get('bankName')?.value
    };

    this.merchantService.createMerchantPayoutAccount(payload, this.messageService).then(data => {
      if(data.Success){
        this.dialogRef.close(true);
        this.showPaymentGridView.emit(true);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Your payment method has been successfully created.' });
      }
      else{
        this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
      }
      this.loading = false;
    }).catch(err => {
       this.loading = false;
       throw err;
    });
  }

  updatePaymentMethod(){
    const payload: UpdateMerchantPayoutAccountRequestDto = {
      MerchantId: this.data?.payment?.MerchantId,
      PayoutAccountTypeId: this.paymentFormGroup.get('payoutAccountType')?.value,
      AccountName: this.paymentFormGroup.get('accountName')?.value,
      AccountNumber: this.paymentFormGroup.get('accountNo')?.value.toString(),
      PayServiceId: this.paymentFormGroup.get('bankName')?.value
    };

    this.merchantService.updateMerchantPayoutAccount(payload, this.messageService).then(data => {
      if(data.Success){
        this.dialogRef.close(true);
        this.showPaymentGridView.emit(true);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Your payment method has been successfully updated.' });
      }
      else{
        this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
      }
      this.loading = false;
    }).catch(err => {
       this.loading = false;
       throw err;
    });
  }

  initializeFields(){
    this.paymentFormGroup = new FormGroup({
      payoutAccountType: new FormControl(this.data?.payment?.PayoutAccountTypeId, Validators.required),
      bankName: new FormControl(this.data?.payment?.PayServiceId, Validators.required),
      accountName: new FormControl(
        this.data?.payment?.AccountName,
        [Validators.required, Validators.pattern(Utils.creditcardaccountRegex), Utils.cannotBeOnlyNumbersOrSpaces]
      ),
      accountNo: new FormControl(this.data?.payment?.AccountNumber, [Validators.required, Validators.pattern(/^\d{10,12}$/)])
    });

    if(!this.data?.payment){
      this.paymentFormGroup.get('bankName')?.disable();
      this.paymentFormGroup.get('accountName')?.disable();
      this.paymentFormGroup.get('accountNo')?.disable();
    }
    else{
      this.selectedPayoutAccountTypeValue = this.data?.payment?.PayoutAccountTypeId;
      this.onSelectPayoutAccountType();
    }
  }

  onSelectPayoutAccountType(){
    switch(this.selectedPayoutAccountTypeValue)
      {
        case PayoutAccountTypeEnum.Bank:
          this.paymentMethods = PayServiceTypeModel.CommonPaymentMethods.filter(x => x.type === PayoutAccountTypeEnum.Bank);
          break;
        case PayoutAccountTypeEnum.EWallet:
          this.paymentMethods = PayServiceTypeModel.CommonPaymentMethods.filter(x => x.type === PayoutAccountTypeEnum.EWallet);
          break;
        default:
          throw new Error(`Unsupported Payout Account Type ${this.selectedPaymentMethodValue}`);
      }
      this.enablePaymentDetailFields();
  }

  enablePaymentDetailFields(){
    this.paymentFormGroup.get('bankName')?.enable();
    this.paymentFormGroup.get('accountName')?.enable();
    this.paymentFormGroup.get('accountNo')?.enable();
  }
}
